import { toFixed, debounce } from '@teko/utils';
import { useState, useEffect, useCallback, useMemo } from 'react';

const DELAY = 300;
const useQuantity = ({
  addToCart,
  baseQuantity = 0,
  precision = 0,
  shouldRoundValue = true
}) => {
  const [quantity, setQuantity] = useState((addToCart == null ? void 0 : addToCart.quantityInCart) || baseQuantity);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (addToCart && !loading) {
      setQuantity(addToCart.quantityInCart || baseQuantity);
    }
  }, [addToCart]);
  const updateQuantity = useCallback(async (quantity2) => {
    var _a;
    const normalizedQuantity = toFixed({
      value: quantity2,
      precision,
      shouldRoundValue
    });
    if (normalizedQuantity === 0) {
      await ((_a = addToCart == null ? void 0 : addToCart.onRemoveItem) == null ? void 0 : _a.call(addToCart));
    } else {
      await (addToCart == null ? void 0 : addToCart.onChangeQuantity(normalizedQuantity));
    }
  }, [addToCart, precision, shouldRoundValue]);
  const handleUpdateQuantity = async () => {
    if (quantity && quantity !== (addToCart == null ? void 0 : addToCart.quantityInCart)) {
      await updateQuantity(quantity);
    } else {
      setQuantity((addToCart == null ? void 0 : addToCart.quantityInCart) || baseQuantity);
    }
  };
  const debounceChange = useMemo(() => {
    const debounceUpdate = debounce(async (quantity2, onError) => {
      try {
        setLoading(true);
        await updateQuantity(quantity2);
      } catch (error) {
        onError == null ? void 0 : onError(error);
      } finally {
        setLoading(false);
      }
    }, DELAY);
    return (newQuantity) => {
      setQuantity(newQuantity);
      debounceUpdate(newQuantity, () => setQuantity((addToCart == null ? void 0 : addToCart.quantityInCart) || baseQuantity));
    };
  }, [addToCart == null ? void 0 : addToCart.quantityInCart, baseQuantity, updateQuantity]);
  const onIncrease = () => {
    debounceChange(quantity + 1);
  };
  const onDecrease = () => {
    debounceChange(quantity - 1);
  };
  return {
    quantity,
    setQuantity,
    handleUpdateQuantity,
    onIncrease,
    onDecrease,
    loading
  };
};

export { useQuantity };
